/**
 * GoldFoilFrame — premium animated gold-foil ring around any child element.
 *
 * Usage:
 *   <GoldFoilFrame rounded="full" inset={3} sheen>
 *     <img src="..." className="h-28 w-28 rounded-full object-cover" />
 *   </GoldFoilFrame>
 *
 * The frame itself does no layout — it positions a foil ring around its
 * children using absolute positioning. The child sets the shape (rounded-full
 * for circles, rounded-3xl for cards, etc).
 *
 * Props:
 *  - rounded: "full" | "3xl" | "2xl" | "xl" — must match the child's corner radius.
 *  - inset:   width of the foil ring in px (default 3).
 *  - sheen:   if true, plays a single diagonal sheen sweep on mount.
 */

const RADIUS_MAP = {
  full: "rounded-full",
  "3xl": "rounded-[1.65rem]",
  "2xl": "rounded-[1.15rem]",
  xl: "rounded-[0.9rem]",
};

const FOIL_GRADIENT =
  "conic-gradient(from 0deg, #B8821D 0%, #F1D182 12%, #D4A24A 25%, #FFF1B8 38%, #B8821D 50%, #F1D182 62%, #D4A24A 75%, #FFF1B8 88%, #B8821D 100%)";

export default function GoldFoilFrame({
  children,
  rounded = "full",
  inset = 3,
  sheen = false,
  className = "",
}) {
  const ringClass = RADIUS_MAP[rounded] || RADIUS_MAP.full;
  return (
    <div className={`relative inline-block ${className}`}>
      <div
        aria-hidden
        className={`absolute ${ringClass} overflow-hidden pointer-events-none`}
        style={{ inset: -inset }}
      >
        <div
          className="absolute -inset-[60%]"
          style={{
            background: FOIL_GRADIENT,
            animation: "iwmc-foil-spin 9s linear infinite",
          }}
        />
      </div>
      {sheen && (
        <div
          aria-hidden
          className={`absolute ${ringClass} overflow-hidden pointer-events-none`}
          style={{ inset: -inset }}
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, transparent 35%, rgba(255,241,184,0.7) 50%, transparent 65%)",
              mixBlendMode: "screen",
              animation: "iwmc-foil-sheen 2.6s ease-out 0.5s 1 both",
            }}
          />
        </div>
      )}
      <div className="relative">{children}</div>
      <style>{`
        @keyframes iwmc-foil-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes iwmc-foil-sheen {
          0%   { transform: translateX(-100%); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-hidden][style*="iwmc-foil-spin"],
          [aria-hidden][style*="iwmc-foil-sheen"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
